/**
 * Trovva — Brain Chat Widget
 *
 * Floating chat panel that connects directly to the AI Brain (/api/brain).
 * Unlike AskAIDrawer (module-scoped), this is the full conversational brain
 * with access to entire CRM, deals, tasks, calendar — everything.
 *
 * Features:
 *   - Conversation memory (conversation_id persists across messages)
 *   - Typing indicator
 *   - Pending confirmation display (Tier 3 actions)
 *   - Suggested prompts for first-time users
 *   - Minimize/expand toggle
 *   - Mobile-responsive
 *
 * Usage:
 *   <BrainChat user={user} demoMode={demoMode} />
 *   Rendered in App.jsx — always present when logged in.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, X, Minimize2, Maximize2, RotateCcw, User, ChevronDown, Zap } from 'lucide-react';
import TrovvaIcon from '../common/TrovvaIcon';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const SUGGESTED_PROMPTS = [
  "What's going on today?",
  "Who are my hottest leads?",
  "Any compliance deadlines this week?",
  "Show my pipeline stats",
  "What's overdue?",
  "How is my team doing?",
];

export default function BrainChat({ user, demoMode, externalOpen, externalMessage, onExternalHandled, activeTab }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input when opened
  useEffect(() => {
    if (open && !minimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimized]);

  // Clear unread when opening
  useEffect(() => {
    if (open && !minimized) setUnread(0);
  }, [open, minimized]);

  // Handle external open/message from Welcome Hero
  useEffect(() => {
    if (externalOpen) {
      setOpen(true);
      setMinimized(false);
      if (externalMessage) {
        // Small delay so the chat panel renders first
        setTimeout(() => send(externalMessage), 150);
      }
      onExternalHandled?.();
    }
  }, [externalOpen]);

  const send = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setError(null);
    setInput('');
    setPendingConfirmation(null);

    // Optimistic user message
    setMessages(prev => [...prev, { role: 'user', content: q, time: new Date() }]);
    setSending(true);

    try {
      if (demoMode) {
        // Demo mode — fake response
        await new Promise(r => setTimeout(r, 1200));
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I'm in demo mode — connect your account to use the full AI brain. In production, I'd look up your real CRM data, check compliance deadlines, and manage your pipeline.\n\nAI-generated · Verify compliance and financial details independently.",
          time: new Date(),
        }]);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const payload = {
        message: q,
        user_id: session.user.id,
        channel: 'web',
        current_page: activeTab || 'dashboard',
      };
      if (conversationId) payload.conversation_id = conversationId;

      const r = await fetch('/api/brain', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await r.json();
      if (!r.ok && r.status !== 200) throw new Error(data.error || `Failed (${r.status})`);

      // Store conversation_id for threading
      if (data.conversation_id) setConversationId(data.conversation_id);

      // Track pending confirmations
      if (data.pending_confirmation) {
        setPendingConfirmation(data.pending_confirmation);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply || '(no response)',
        tools_used: data.tools_used || [],
        time: new Date(),
      }]);

      if (!open || minimized) setUnread(prev => prev + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [input, sending, conversationId, demoMode, open, minimized, activeTab]);

  const handleConfirm = useCallback(async (answer) => {
    if (!pendingConfirmation) return;
    setMessages(prev => [...prev, { role: 'user', content: answer, time: new Date() }]);
    setSending(true);
    setPendingConfirmation(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');

      const r = await fetch('/api/brain', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: session.user.id,
          confirm_action: answer.toLowerCase() === 'yes' ? 'yes' : 'no',
          message: answer,
          channel: 'web',
          conversation_id: conversationId,
        }),
      });

      const data = await r.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply || 'Done.',
        time: new Date(),
      }]);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [pendingConfirmation, conversationId]);

  const reset = () => {
    setMessages([]);
    setConversationId(null);
    setPendingConfirmation(null);
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ── Render ─────────────────────────────────────────────────

  // Floating action button (when chat is closed)
  if (!open) {
    return (
      <>
        <style>{`
          @keyframes brainPulse {
            0%, 100% { box-shadow: 0 4px 20px rgba(27,94,84,0.4); }
            50% { box-shadow: 0 4px 28px rgba(27,94,84,0.6), 0 0 0 8px rgba(27,94,84,0.08); }
          }
        `}</style>
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: 20, right: 20, zIndex: 500,
            height: 52, borderRadius: 26,
            padding: '0 20px 0 16px',
            background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
            color: 'white', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'brainPulse 3s ease-in-out infinite',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          title="Talk to Trovva Brain"
        >
          <TrovvaIcon size={22} animate />
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>Ask Trovva</span>
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: theme.colors.danger, color: 'white',
              width: 20, height: 20, borderRadius: 10,
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{unread}</span>
          )}
        </button>
      </>
    );
  }

  // Minimized bar
  if (minimized) {
    return (
      <div
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 500,
          width: 280, borderRadius: 14,
          background: theme.colors.primary, color: 'white',
          padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          cursor: 'pointer',
        }}
        onClick={() => setMinimized(false)}
      >
        <TrovvaIcon size={18} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>Trovva Brain</span>
        {unread > 0 && (
          <span style={{
            background: theme.colors.danger, color: 'white',
            padding: '2px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
          }}>{unread}</span>
        )}
        <Maximize2 size={14} style={{ opacity: 0.7 }} />
        <X size={14} style={{ opacity: 0.7 }} onClick={e => { e.stopPropagation(); setOpen(false); setMinimized(false); }} />
      </div>
    );
  }

  // Full chat panel
  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 500,
      width: 400, maxWidth: 'calc(100vw - 40px)',
      height: 560, maxHeight: 'calc(100vh - 100px)',
      borderRadius: 16,
      background: theme.colors.card,
      boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      border: `1px solid ${theme.colors.border}`,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
        color: 'white',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <TrovvaIcon size={20} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Trovva Brain</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>
            {conversationId ? 'Active conversation' : 'Full CRM access'}
          </div>
        </div>
        {messages.length > 0 && (
          <button onClick={reset} title="New conversation" style={headerBtn()}>
            <RotateCcw size={13} />
          </button>
        )}
        <button onClick={() => setMinimized(true)} title="Minimize" style={headerBtn()}>
          <Minimize2 size={13} />
        </button>
        <button onClick={() => { setOpen(false); setMinimized(false); }} title="Close" style={headerBtn()}>
          <X size={14} />
        </button>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', padding: '14px 16px',
        minHeight: 0, background: '#F8FAFB',
      }}>
        {/* Empty state with suggestions */}
        {messages.length === 0 && (
          <div style={{ padding: '16px 0' }}>
            <div style={{
              textAlign: 'center', marginBottom: 16,
              fontSize: 13, color: theme.colors.textLight,
            }}>
              I have full access to your CRM, deals, tasks, and calendar. Ask me anything.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
              {SUGGESTED_PROMPTS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  style={{
                    padding: '7px 12px', borderRadius: 20,
                    border: `1px solid ${theme.colors.border}`,
                    background: 'white', cursor: 'pointer',
                    fontSize: 12, color: theme.colors.text,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.target.style.background = theme.colors.primary; e.target.style.color = 'white'; e.target.style.borderColor = theme.colors.primary; }}
                  onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.color = theme.colors.text; e.target.style.borderColor = theme.colors.border; }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex', gap: 8, marginBottom: 12,
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
          }}>
            {/* Avatar */}
            <div style={{
              width: 28, height: 28, borderRadius: 14, flexShrink: 0,
              background: msg.role === 'user'
                ? theme.colors.accent
                : `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
              color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12,
            }}>
              {msg.role === 'user' ? <User size={14} /> : <TrovvaIcon size={14} />}
            </div>

            {/* Bubble */}
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? theme.colors.primary : 'white',
              color: msg.role === 'user' ? 'white' : theme.colors.text,
              fontSize: 13, lineHeight: 1.5,
              boxShadow: msg.role === 'user' ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
              border: msg.role === 'user' ? 'none' : `1px solid ${theme.colors.border}`,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {formatMessage(msg.content)}
              {msg.tools_used?.length > 0 && (
                <div style={{
                  marginTop: 8, paddingTop: 6,
                  borderTop: `1px solid ${msg.role === 'user' ? 'rgba(255,255,255,0.2)' : theme.colors.border}`,
                  fontSize: 10, opacity: 0.6,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <Zap size={10} />
                  {msg.tools_used.length} tool{msg.tools_used.length > 1 ? 's' : ''} used
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {sending && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 14, flexShrink: 0,
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <TrovvaIcon size={14} />
            </div>
            <div style={{
              padding: '12px 16px', borderRadius: '14px 14px 14px 4px',
              background: 'white', border: `1px solid ${theme.colors.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <TypingDots />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: 10,
            background: '#FEF2F2', color: theme.colors.danger,
            fontSize: 12, marginBottom: 12,
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Confirmation buttons (Tier 3) */}
      {pendingConfirmation && (
        <div style={{
          padding: '10px 16px',
          background: '#FFFBEB',
          borderTop: `1px solid ${theme.colors.warning}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ flex: 1, fontSize: 12, color: theme.colors.text }}>
            Confirm action?
          </span>
          <button onClick={() => handleConfirm('yes')} style={confirmBtn(theme.colors.success)}>
            Yes
          </button>
          <button onClick={() => handleConfirm('no')} style={confirmBtn(theme.colors.danger)}>
            No
          </button>
        </div>
      )}

      {/* Input area */}
      <div style={{
        padding: '12px 16px',
        borderTop: `1px solid ${theme.colors.border}`,
        display: 'flex', gap: 8, alignItems: 'flex-end',
        background: 'white',
      }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={pendingConfirmation ? 'Reply yes or no...' : 'Ask Trovva anything...'}
          rows={1}
          style={{
            flex: 1, resize: 'none', border: `1px solid ${theme.colors.border}`,
            borderRadius: 12, padding: '10px 14px', fontSize: 13,
            fontFamily: 'inherit', outline: 'none',
            maxHeight: 80, overflow: 'auto',
            transition: 'border-color 0.2s',
          }}
          onFocus={e => e.target.style.borderColor = theme.colors.primary}
          onBlur={e => e.target.style.borderColor = theme.colors.border}
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || sending}
          style={{
            width: 38, height: 38, borderRadius: 12,
            background: input.trim() && !sending
              ? theme.colors.primary
              : theme.colors.border,
            color: 'white', border: 'none', cursor: input.trim() && !sending ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.2s',
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}


// ── Typing dots animation ─────────────────────────────────────

function TypingDots() {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setFrame(f => (f + 1) % 4), 400);
    return () => clearInterval(iv);
  }, []);
  const dots = '.'.repeat(frame || 1);
  return (
    <span style={{ color: theme.colors.textLight, fontSize: 13, letterSpacing: 2 }}>
      Thinking{dots}
    </span>
  );
}


// ── Format message with basic markdown support ────────────────

function renderMarkdown(raw) {
  if (!raw) return '';
  // Convert markdown to simple HTML:
  // **bold** → <strong>, *italic* → <em>, `code` → <code>
  // - bullet lines → <li>, newlines → <br>
  let html = raw
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')  // escape HTML
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')                     // **bold**
    .replace(/\*(.+?)\*/g, '<em>$1</em>')                                 // *italic*
    .replace(/`(.+?)`/g, '<code style="background:#F1F5F9;padding:1px 4px;border-radius:3px;font-size:12px">$1</code>');  // `code`

  // Convert bullet lines (- item or • item) to list items
  const lines = html.split('\n');
  let inList = false;
  const processed = [];
  for (const line of lines) {
    const bulletMatch = line.match(/^\s*[-•]\s+(.+)/);
    if (bulletMatch) {
      if (!inList) { processed.push('<ul style="margin:4px 0 4px 16px;padding:0;list-style:disc">'); inList = true; }
      processed.push(`<li style="margin:2px 0;font-size:13px">${bulletMatch[1]}</li>`);
    } else {
      if (inList) { processed.push('</ul>'); inList = false; }
      if (line.trim() === '') {
        processed.push('<br/>');
      } else {
        processed.push(line);
      }
    }
  }
  if (inList) processed.push('</ul>');
  return processed.join('\n').replace(/\n(?!<)/g, '<br/>');
}

function formatMessage(text) {
  if (!text) return '';

  // Split off the AI disclaimer footer if present
  const disclaimerMarker = 'AI-generated';
  const idx = text.indexOf(disclaimerMarker);
  let body = text;
  let disclaimer = null;
  if (idx > 0) {
    body = text.slice(0, idx).trimEnd();
    disclaimer = text.slice(idx);
  }

  return (
    <>
      <span dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }} />
      {disclaimer && (
        <div style={{
          marginTop: 10, paddingTop: 8,
          borderTop: '1px solid rgba(0,0,0,0.06)',
          fontSize: 10, color: '#94A3B8', lineHeight: 1.4,
          letterSpacing: '0.01em',
        }}>
          {disclaimer}
        </div>
      )}
    </>
  );
}


// ── Style helpers ─────────────────────────────────────────────

function headerBtn() {
  return {
    background: 'rgba(255,255,255,0.15)',
    border: 'none', borderRadius: 8,
    width: 28, height: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', cursor: 'pointer',
    transition: 'background 0.2s',
  };
}

function confirmBtn(color) {
  return {
    padding: '6px 16px', borderRadius: 8,
    background: color, color: 'white',
    border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
  };
}
